/**
 * Thunder Fist — Plan 4a Task 4.1
 *
 * Directional skillshot. Targets the first opponent in range along the cast direction.
 * - Range: THUNDER_FIST_RANGE (3) landable graph tiles along walk edges.
 * - On hit: 30 damage via applyDamage.
 * - Stone Skin block: consumed, item_blocked_stoneskin emitted, no damage.
 * - No target in range: item_missed emitted.
 *
 * Self-registers via addItem(thunderFist) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import type { BoardGraph } from '@shared/board/graph';
import {
  directionBetweenNodes,
  findWalkPath,
  walkForwardGraphPath,
  walkForwardRay,
  type ForwardRayResult,
} from '@shared/board/targeting';
import { appendEvent } from '../event-log';
import { applyDamage } from '../damage';
import { addItem } from './registry';
import { tryBlockWithStoneSkin } from '../stone-skin-block';

/** Maximum landable graph-tile range for Thunder Fist to reach a target. */
export const THUNDER_FIST_RANGE = 3;

/**
 * Extended cast context that optionally carries the BoardGraph for position look-up.
 * Cast callers from server/game-loop pass the graph; test stubs can also pass it.
 */
type ThunderFistCtx = ItemCastCtx & { graph?: BoardGraph };

function firstWalkPathStep(
  graph: BoardGraph,
  fromNodeId: string,
  toNodeId: string,
): string | undefined {
  const result = findWalkPath(graph, fromNodeId, toNodeId);
  return result.path.length >= 2 ? result.path[1] : undefined;
}

function resolveGraphPathRay(
  graph: BoardGraph,
  state: GameState,
  casterId: string,
  firstNodeId: string | undefined,
): ForwardRayResult | null {
  const caster = state.players.get(casterId);
  if (caster == null || firstNodeId == null) return null;
  const ray = walkForwardGraphPath(graph, caster.nodeId, firstNodeId, {
    maxLandableTiles: THUNDER_FIST_RANGE,
  });
  return ray.path.length > 0 ? ray : null;
}

function resolveTargetPlayerFirstStep(
  graph: BoardGraph,
  state: GameState,
  casterId: string,
  targetPlayerId: string | undefined,
): string | undefined {
  if (targetPlayerId == null || targetPlayerId === casterId) return undefined;
  const caster = state.players.get(casterId);
  const target = state.players.get(targetPlayerId);
  if (caster == null || target == null || target.isDead) return undefined;
  return firstWalkPathStep(graph, caster.nodeId, target.nodeId);
}

function pathUntilNode(path: readonly string[], nodeId: string): string[] {
  const index = path.indexOf(nodeId);
  return index < 0 ? [...path] : path.slice(0, index + 1);
}

export const thunderFist: Item = {
  id: 'thunder_fist',
  name: 'Thunder Fist',
  description:
    'Fires a directional punch that deals 30 damage to the first player in its path (range: 3 tiles). Blocked by Stone Skin.',
  targetType: 'direction',
  category: 'simple',
  rarity: 'uncommon',
  blockedByStoneSkin: true,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;
    const requestedDirection = ctx.target.direction;
    const direction = requestedDirection ?? { dx: 1, dy: 0 };
    const graph = (ctx as ThunderFistCtx).graph;

    // Step 1: Validate caster
    const caster = state.players.get(casterId);
    if (!caster) throw new Error(`Thunder Fist: caster not found: ${casterId}`);
    if (!graph) throw new Error('Thunder Fist: graph required for direction-based traversal');

    const explicitFirstNodeId = ctx.target.targetNodeId;
    const explicitRay = resolveGraphPathRay(graph, state, casterId, explicitFirstNodeId);
    const derivedFirstNodeId =
      explicitRay == null && requestedDirection == null
        ? resolveTargetPlayerFirstStep(graph, state, casterId, ctx.target.targetPlayerId)
        : undefined;
    const derivedRay =
      explicitRay == null ? resolveGraphPathRay(graph, state, casterId, derivedFirstNodeId) : null;
    const graphRay = explicitRay ?? derivedRay;
    const selectedFirstNodeId =
      explicitRay != null
        ? explicitFirstNodeId
        : derivedRay != null
          ? derivedFirstNodeId
          : undefined;
    const castDirection =
      selectedFirstNodeId == null
        ? direction
        : (directionBetweenNodes(graph, caster.nodeId, selectedFirstNodeId) ?? direction);

    // Step 2: Emit item_cast
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'thunder_fist',
      ...(ctx.target.targetPlayerId != null ? { targetId: ctx.target.targetPlayerId } : {}),
      ...(selectedFirstNodeId != null ? { targetNodeId: selectedFirstNodeId } : {}),
      direction: castDirection,
    });

    // Step 3: Find the first living opponent on the forward graph ray.
    let hitTargetId: string | null = null;
    let hitNodeId: string | null = null;
    let ray = graphRay;

    if (ray == null) {
      ray = walkForwardRay(graph, caster.nodeId, direction, {
        maxLandableTiles: THUNDER_FIST_RANGE,
      });
    }

    for (const nodeId of ray.path) {
      for (const playerId of state.turnOrder) {
        const player = state.players.get(playerId);
        if (player == null) continue;
        if (playerId === casterId) continue;
        if (player.isDead) continue;
        if (player.nodeId !== nodeId) continue;
        hitTargetId = playerId;
        hitNodeId = nodeId;
        break;
      }
      if (hitTargetId !== null) break;
    }

    // Step 4: Resolve hit / block / miss
    if (hitTargetId !== null && hitNodeId !== null) {
      if (
        tryBlockWithStoneSkin(state, hitTargetId, {
          actorId: casterId,
          sourceId: 'thunder_fist',
        })
      ) {
        return { success: true, blockedByStoneSkin: true };
      }

      // Hit lands: emit item_hit, then apply 30 damage.
      appendEvent(state, {
        type: 'item_hit',
        playerId: casterId,
        casterId,
        itemId: 'thunder_fist',
        targetId: hitTargetId,
        ...(selectedFirstNodeId != null ? { targetNodeId: selectedFirstNodeId } : {}),
        nodeId: hitNodeId,
        path: pathUntilNode(ray.path, hitNodeId),
        toNodeId: hitNodeId,
        direction: castDirection,
        damageDealt: 30,
      });

      applyDamage(state, hitTargetId, 30, 'item_thunder_fist', casterId);
      return { success: true, damageDealt: 30 };
    }

    // Step 5: No target found — emit missed
    appendEvent(state, {
      type: 'item_missed',
      playerId: casterId,
      casterId,
      itemId: 'thunder_fist',
      ...(ctx.target.targetPlayerId != null ? { targetId: ctx.target.targetPlayerId } : {}),
      ...(selectedFirstNodeId != null ? { targetNodeId: selectedFirstNodeId } : {}),
      path: ray.path,
      toNodeId: ray.finalNodeId,
      direction: castDirection,
    });
    return { success: true, missed: true };
  },
};

// Self-register at module load
addItem(thunderFist);
